import React, { useContext, useEffect, useState } from 'react';

import AJAX from 'common/AJAX';
import TextToggle from 'common/common/TextToggle';
import { TrackEventContext } from 'common/containers/EventContainer';
import { ShowIntercomContext } from 'common/containers/IntercomContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import ButtonV2 from 'common/ui/ButtonV2';
import { H1, P } from 'common/ui/Text';
import getCookies from 'common/util/getCookies';
import isFree from 'common/util/isFree';
import { GrowthPlanID } from 'common/util/isGrowth';
import isStarter, { StarterPlanID } from 'common/util/isStarter';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import validateInput from 'common/validateInput';

import ExistingCompaniesForm from './ExistingCompaniesForm';
import NewCompanyForm from './NewCompanyForm';

import type { CompanyObject as Company, Viewer } from 'common/api/endpoints/viewer';

import 'css/components/company/_CreateOrUpdateCompanyForm.scss';

type Props = {
  trialPlan: string;
};

export const FormErrors = {
  'already started trial': 'This company already has an active trial',
  invalidOption: 'Please select an option',
  invalidCompanyName: 'Please enter a valid company name.',
  invalidSubdomainName: 'Please enter a valid subdomain.',
  'not authorized': "You don't have the permissions to start a trial",
  'not eligible': 'This account has already been on a trial. Contact support to start a trial.',
  'email domain is not trusted':
    'The email linked to your user is not allowed to create a new company. Please, change your email to continue.',
  'subdomain taken': 'This subdomain is already taken. Please choose a different one.',
  'slow down':
    'You are trying to create too many accounts. Please wait a few minutes before trying again.',
  default: 'Something went wrong, please contact our team for support.',
};

const formOptions = [
  {
    label: 'Existing Company',
    value: 'existing_company',
  },
  {
    label: 'New Company',
    value: 'new_company',
  },
];

const CreateOrUpdateCompanyForm = ({ trialPlan }: Props) => {
  const viewer = useContext<Viewer>(ViewerContext);
  const showIntercom = useContext(ShowIntercomContext);
  const trackEvent = useContext(TrackEventContext);

  const [selectedFormOption, setFormOption] = useState(formOptions[0].value);
  const [fieldError, setFieldError] = useState({
    invalidOption: false,
    invalidCompanyName: false,
    invalidSubdomainName: false,
  });
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);

  // Existing company state
  const [selectedCompanySubdomain, setSelectedCompanySubdomain] = useState<string>();

  // New company state
  const [companyName, setCompanyName] = useState('');
  const [companySubdomain, setCompanySubdomain] = useState('');

  let planName = 'Starter';
  if (trialPlan === 'growth') {
    planName = 'Growth';
  }

  useEffect(() => {
    setFormError('');
    resetErrors();
    setLoading(false);
  }, [selectedFormOption]);

  const resetErrors = () => {
    setFieldError({
      invalidOption: false,
      invalidCompanyName: false,
      invalidSubdomainName: false,
    });
    setFormError('');
  };

  // Technically we should never get here if there are no viewer.companies, we check this
  // in Register.js
  const validCompanies = viewer.companies
    ? viewer.companies.reduce((acc: string[], company: Company) => {
        if (
          (trialPlan === 'starter' && (!company.planID || isFree(company.planID))) ||
          (trialPlan === 'growth' &&
            (!company.planID || isFree(company.planID) || isStarter(company.planID)))
        ) {
          acc.push(company._id);
        }

        return acc;
      }, [])
    : [];

  const onSuccess = (subdomain: string, route: string) => {
    const protocol = window.location.protocol + '//';
    const host = '.' + window.location.host;
    const subdomainURL = protocol + subdomain + host + route;
    window.location.assign(subdomainURL);
  };

  const upgradeExistingCompany = async () => {
    if (!selectedCompanySubdomain) {
      setFieldError({
        ...fieldError,
        invalidOption: true,
      });
      return;
    }

    resetErrors();

    const planID = trialPlan === 'starter' ? StarterPlanID : GrowthPlanID;
    const company = viewer.companies?.find(
      (company) => company.subdomain === selectedCompanySubdomain
    );
    setLoading(true);
    const response = await AJAX.post('/api/billing/startPlanTrial', {
      companyID: company?._id,
      planID,
    });

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
      errors: FormErrors,
    });

    if (error) {
      setFormError(error.message);
      setLoading(false);
      return;
    }

    setLoading(false);

    if (company?.subdomain) {
      onSuccess(company.subdomain, '/admin/settings/billing');
    }
  };

  const createCompany = async () => {
    trackEvent('Submitted Register Form');
    resetErrors();
    const invalidCompanyName = !companyName || !validateInput.companyName(companyName);
    const invalidSubdomainName = !companySubdomain || !validateInput.subdomain(companySubdomain);
    if (invalidCompanyName || invalidSubdomainName) {
      setFieldError({
        ...fieldError,
        invalidCompanyName: invalidCompanyName,
        invalidSubdomainName: invalidSubdomainName,
      });
      return;
    }

    trackEvent('Completed Registration');
    trackEvent('Made Register Request');

    setLoading(true);

    const [hubspotutk] = getCookies(['hubspotutk']);
    const response = await AJAX.post('/api/company/create', {
      companyName,
      hubspotutk,
      subdomain: companySubdomain,
      trialPlan,
    });

    const { error, parsedResponse } = parseAPIResponse(response, {
      isSuccessful: (response: { isVIP: boolean; qualified: boolean; success: boolean }) =>
        response.success,
      errors: FormErrors,
    });

    if (error) {
      setFormError(error.message);
      setLoading(false);
      return;
    }

    if (parsedResponse?.success) {
      trackEvent('Registered Company');

      // eslint-disable-next-line
      // @ts-ignore
      window.qp?.('track', 'CompleteRegistration');

      if (parsedResponse?.isVIP) {
        trackEvent('Registered VIP Company');
      }

      if (parsedResponse?.qualified) {
        trackEvent('Registered Qualified Company');
      }

      onSuccess(companySubdomain, '/admin/onboarding/redirect');
    }
  };

  const startTrial = async () => {
    if (selectedFormOption === formOptions[0].value && viewer.companies) {
      await upgradeExistingCompany();
    } else {
      await createCompany();
    }
  };

  return (
    <div className="createOrUpdateCompanyFormContainer">
      <form className="createOrUpdateCompanyForm">
        <div className="formHeader">
          <H1 className="header" variant="headingLg">
            {planName} Plan Trial
          </H1>
          <P className="description" variant="bodyMd">
            Select an existing company or start a {planName.toLowerCase()} trial for a new company.
          </P>
          <TextToggle
            className="formOptions"
            onChange={loading ? () => null : setFormOption}
            options={formOptions}
            selected={selectedFormOption}
            variant="partialBorder"
          />
        </div>
        <div className="formContent">
          {selectedFormOption === formOptions[0].value && viewer.companies?.length ? (
            <ExistingCompaniesForm
              companies={viewer.companies}
              fieldError={fieldError}
              selectedCompany={selectedCompanySubdomain}
              setSelectedCompany={setSelectedCompanySubdomain}
              validCompanies={validCompanies}
            />
          ) : (
            <NewCompanyForm
              companyName={companyName}
              fieldError={fieldError}
              setCompanyName={setCompanyName}
              setCompanySubdomain={setCompanySubdomain}
              submitting={loading}
            />
          )}
        </div>
        <div className="formFooter">
          <ButtonV2
            color="primary"
            onClick={startTrial}
            className="startButton"
            loading={loading}
            size="medium">
            Start trial
          </ButtonV2>
          {formError || fieldError.invalidOption ? (
            <P variant="bodySm" className="error">
              {formError || FormErrors.invalidOption}
            </P>
          ) : null}
        </div>
      </form>
      <P className="contact" variant="bodyMd">
        Having issues?{' '}
        <button className="showIntercomButton" onClick={showIntercom}>
          Contact support
        </button>
      </P>
    </div>
  );
};

export default CreateOrUpdateCompanyForm;
